import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']

export default function Parametres() {
  const { praticien } = useAuth()
  const [form, setForm] = useState(emptyForm())
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // --- Fiche site web ---
  const [fiche, setFiche] = useState(emptyFiche())
  const [savingFiche, setSavingFiche] = useState(false)
  const [ficheSaved, setFicheSaved] = useState(false)
  const [uploading, setUploading] = useState(null)

  // --- Plages horaires ---
  const [plages, setPlages] = useState([])
  const [savingPlages, setSavingPlages] = useState(false)
  const [plagesSaved, setPlagesSaved] = useState(false)

  function emptyForm() {
    return {
      prenom: '', nom: '', email: '', telephone: '',
      adresse_cabinet: '', code_postal: '', ville: '',
      siret: '', numero_rpps: '', adeli: '', specialite: '',
      tarif_base: 60, site_web: '', iban: '',
    }
  }

  function emptyFiche() {
    return {
      photo_portrait_url: '', photo_piece_url: '',
      presentation_courte: '', presentation_longue: '',
      formations: '', actualite: '',
      actualite_visible: false, fiche_visible: true,
    }
  }

  useEffect(() => {
    if (!praticien) return
    setForm({
      prenom: praticien.prenom || '',
      nom: praticien.nom || '',
      email: praticien.email || '',
      telephone: praticien.telephone || '',
      adresse_cabinet: praticien.adresse_cabinet || '',
      code_postal: praticien.code_postal || '',
      ville: praticien.ville || '',
      siret: praticien.siret || '',
      numero_rpps: praticien.numero_rpps || '',
      adeli: praticien.adeli || '',
      specialite: praticien.specialite || '',
      tarif_base: praticien.tarif_base || 60,
      site_web: praticien.site_web || '',
      iban: praticien.iban || '',
    })
  }, [praticien])

  useEffect(() => {
    if (!praticien) return
    supabase.from('fiches_publiques').select('*').eq('praticien_id', praticien.id).maybeSingle()
      .then(({ data }) => {
        if (data) setFiche({
          photo_portrait_url: data.photo_portrait_url || '',
          photo_piece_url: data.photo_piece_url || '',
          presentation_courte: data.presentation_courte || '',
          presentation_longue: data.presentation_longue || '',
          formations: data.formations || '',
          actualite: data.actualite || '',
          actualite_visible: !!data.actualite_visible,
          fiche_visible: data.fiche_visible !== false,
        })
      })
  }, [praticien])

  useEffect(() => {
    if (!praticien) return
    supabase.from('plages_horaires').select('*').eq('praticien_id', praticien.id).order('jour').order('heure_debut')
      .then(({ data }) => {
        if (data && data.length > 0) {
          setPlages(data)
        } else {
          // Initialiser avec une structure vide pour chaque jour
          setPlages(JOURS.map((_, i) => ({
            jour: i + 1, heure_debut: '09:00', heure_fin: '18:00', duree_rdv: 45, actif: false, praticien_id: praticien.id
          })))
        }
      })
  }, [praticien])

  async function savePlages() {
    setSavingPlages(true)
    for (const p of plages) {
      if (p.id) {
        await supabase.from('plages_horaires').update({
          heure_debut: p.heure_debut, heure_fin: p.heure_fin,
          duree_rdv: p.duree_rdv, actif: p.actif
        }).eq('id', p.id)
      } else {
        await supabase.from('plages_horaires').insert({ ...p, praticien_id: praticien.id })
      }
    }
    // Recharger
    const { data } = await supabase.from('plages_horaires').select('*').eq('praticien_id', praticien.id).order('jour').order('heure_debut')
    if (data) setPlages(data)
    setSavingPlages(false)
    setPlagesSaved(true)
    setTimeout(() => setPlagesSaved(false), 2000)
  }

  function updatePlage(jour, field, value) {
    setPlages(prev => prev.map(p => p.jour === jour ? { ...p, [field]: value } : p))
  }

  async function save() {
    setSaving(true)
    const { error } = await supabase.from('praticiens').update(form).eq('id', praticien.id)
    if (error) { alert('Erreur : ' + error.message) }
    else { setSaved(true); setTimeout(() => setSaved(false), 3000) }
    setSaving(false)
  }

  async function uploadSignature(e) {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async (ev) => {
      await supabase.from('praticiens').update({ signature_base64: ev.target.result }).eq('id', praticien.id)
      alert('Signature enregistrée.')
    }
    reader.readAsDataURL(file)
  }

  // Redimensionne et compresse l'image avant envoi (photo "automatique")
  function resizeImage(file, maxDim, quality = 0.85) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        URL.revokeObjectURL(url)
        let { width, height } = img
        if (width > height && width > maxDim) { height = Math.round(height * maxDim / width); width = maxDim }
        else if (height >= width && height > maxDim) { width = Math.round(width * maxDim / height); height = maxDim }
        const canvas = document.createElement('canvas')
        canvas.width = width; canvas.height = height
        canvas.getContext('2d').drawImage(img, 0, 0, width, height)
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('Conversion échouée')), 'image/jpeg', quality)
      }
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image illisible')) }
      img.src = url
    })
  }

  async function uploadPhoto(e, column, maxDim) {
    const file = e.target.files[0]
    if (!file) return
    setUploading(column)
    try {
      const blob = await resizeImage(file, maxDim)
      const path = `${praticien.id}/${column}.jpg`
      const { error: upErr } = await supabase.storage.from('fiches-photos')
        .upload(path, blob, { upsert: true, contentType: 'image/jpeg' })
      if (upErr) throw upErr
      const { data } = supabase.storage.from('fiches-photos').getPublicUrl(path)
      // ?t= force le rafraîchissement de l'aperçu (le chemin est réutilisé)
      setFiche(f => ({ ...f, [column]: data.publicUrl + '?t=' + Date.now() }))
    } catch (err) {
      alert('Erreur photo : ' + err.message)
    }
    setUploading(null)
    e.target.value = ''
  }

  async function saveFiche() {
    setSavingFiche(true)
    const payload = { praticien_id: praticien.id, ...fiche, updated_at: new Date().toISOString() }
    const { error } = await supabase.from('fiches_publiques').upsert(payload, { onConflict: 'praticien_id' })
    if (error) { alert('Erreur : ' + error.message) }
    else { setFicheSaved(true); setTimeout(() => setFicheSaved(false), 3000) }
    setSavingFiche(false)
  }

  const labelStyle = {borderTop:'0.5px solid var(--border)', paddingTop:16, marginTop:4, marginBottom:16, fontSize:13, fontWeight:500, color:'var(--text-2)', textTransform:'uppercase', letterSpacing:'.04em'}

  function Compteur({ valeur, max }) {
    const n = (valeur || '').length
    return <div style={{fontSize:11, color: n > max ? '#c0392b' : 'var(--text-3)', textAlign:'right', marginTop:2}}>{n} / {max}</div>
  }

  function PhotoBlock({ column, label, maxDim, ronde }) {
    const src = fiche[column]
    return (
      <div style={{marginBottom:18}}>
        <label style={{display:'block', fontSize:13, color:'var(--text-2)', marginBottom:8}}>{label}</label>
        <div style={{display:'flex', alignItems:'center', gap:14}}>
          {src ? (
            <img src={src} alt={label}
              style={{width:ronde?88:120, height:88, borderRadius:ronde?'50%':'var(--radius)', objectFit:'cover', border:'0.5px solid var(--border)'}} />
          ) : (
            <div style={{width:ronde?88:120, height:88, borderRadius:ronde?'50%':'var(--radius)', border:'0.5px dashed var(--border-md)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'var(--text-3)'}}>
              Aucune
            </div>
          )}
          <label style={{cursor:'pointer'}}>
            <span className="btn btn-sm">{uploading===column ? 'Envoi…' : (src ? 'Changer' : 'Ajouter')}</span>
            <input type="file" accept="image/*" disabled={uploading===column}
              onChange={e => uploadPhoto(e, column, maxDim)} style={{display:'none'}} />
          </label>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="card" style={{maxWidth:680, marginBottom:24}}>
        <div style={{fontSize:13, fontWeight:500, color:'var(--text-2)', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:20}}>
          Informations professionnelles
        </div>

        <div className="form-row">
          <div className="form-group"><label>Prénom *</label><input value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})} /></div>
          <div className="form-group"><label>Nom *</label><input value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
          <div className="form-group"><label>Téléphone</label><input value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} /></div>
        </div>
        <div className="form-group" style={{marginBottom:14}}>
          <label>Spécialité</label>
          <input value={form.specialite} onChange={e => setForm({...form, specialite: e.target.value})} placeholder="Ex: Ostéopathe D.O." />
        </div>

        <div style={labelStyle}>Adresse du cabinet</div>
        <div className="form-group" style={{marginBottom:14}}>
          <label>Adresse</label>
          <input value={form.adresse_cabinet} onChange={e => setForm({...form, adresse_cabinet: e.target.value})} />
        </div>
        <div className="form-row">
          <div className="form-group"><label>Code postal</label><input value={form.code_postal} onChange={e => setForm({...form, code_postal: e.target.value})} /></div>
          <div className="form-group"><label>Ville</label><input value={form.ville} onChange={e => setForm({...form, ville: e.target.value})} /></div>
        </div>
        <div className="form-group" style={{marginBottom:14}}>
          <label>Site web</label>
          <input value={form.site_web} onChange={e => setForm({...form, site_web: e.target.value})} placeholder="https://..." />
        </div>

        <div style={labelStyle}>Identifiants professionnels</div>
        <div className="form-row">
          <div className="form-group"><label>N° RPPS</label><input value={form.numero_rpps} onChange={e => setForm({...form, numero_rpps: e.target.value})} /></div>
          <div className="form-group"><label>N° Adeli</label><input value={form.adeli} onChange={e => setForm({...form, adeli: e.target.value})} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>SIRET</label><input value={form.siret} onChange={e => setForm({...form, siret: e.target.value})} /></div>
          <div className="form-group"><label>IBAN (pour les virements)</label><input value={form.iban} onChange={e => setForm({...form, iban: e.target.value})} placeholder="FR76..." /></div>
        </div>

        <div style={labelStyle}>Tarification</div>
        <div className="form-row">
          <div className="form-group">
            <label>Tarif de base (€)</label>
            <input type="number" value={form.tarif_base} onChange={e => setForm({...form, tarif_base: parseFloat(e.target.value)})} />
          </div>
          <div style={{flex:1}} />
        </div>

        <div style={labelStyle}>Signature (pour les factures PDF)</div>
        <div style={{display:'flex', alignItems:'center', gap:14, marginBottom:20}}>
          {praticien?.signature_base64 ? (
            <div style={{border:'0.5px solid var(--border)', borderRadius:'var(--radius)', padding:8, background:'var(--bg)'}}>
              <img src={praticien.signature_base64} alt="Signature" style={{height:48, maxWidth:160, objectFit:'contain'}} />
            </div>
          ) : (
            <div style={{width:160, height:48, border:'0.5px dashed var(--border-md)', borderRadius:'var(--radius)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'var(--text-3)'}}>
              Aucune signature
            </div>
          )}
          <label style={{cursor:'pointer'}}>
            <span className="btn btn-sm">{praticien?.signature_base64 ? 'Changer la signature' : 'Ajouter une signature'}</span>
            <input type="file" accept="image/*" onChange={uploadSignature} style={{display:'none'}} />
          </label>
        </div>

        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          {saved && <span style={{fontSize:13, color:'var(--green-text)'}}>✓ Enregistré</span>}
        </div>
      </div>

      {/* ============================================================
          PLAGES HORAIRES
         ============================================================ */}
      <div className="card" style={{maxWidth:680, marginBottom:24}}>
        <div style={{fontSize:13, fontWeight:500, color:'var(--text-2)', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:8}}>
          Plages horaires (prise de RDV en ligne)
        </div>
        <p style={{fontSize:13, color:'var(--text-3)', marginTop:0, marginBottom:20, lineHeight:1.5}}>
          Définissez vos jours et horaires de consultation. Seuls les jours cochés seront proposés en ligne.
        </p>
        <div style={{display:'flex', flexDirection:'column', gap:8}}>
          {plages.map(p => (
            <div key={p.jour} style={{display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderRadius:'var(--radius)', border:'1px solid var(--border)', background: p.actif ? '#F9FFFE' : 'var(--surface)', opacity: p.actif ? 1 : 0.6}}>
              <input type="checkbox" checked={!!p.actif} onChange={e => updatePlage(p.jour, 'actif', e.target.checked)} style={{flexShrink:0}} />
              <span style={{width:80, fontSize:13, fontWeight:600}}>{JOURS[p.jour - 1]}</span>
              <div style={{display:'flex', alignItems:'center', gap:6, flex:1}}>
                <input type="time" value={p.heure_debut} disabled={!p.actif}
                  onChange={e => updatePlage(p.jour, 'heure_debut', e.target.value)}
                  style={{fontSize:13, padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', width:90}} />
                <span style={{fontSize:12, color:'var(--text-3)'}}>→</span>
                <input type="time" value={p.heure_fin} disabled={!p.actif}
                  onChange={e => updatePlage(p.jour, 'heure_fin', e.target.value)}
                  style={{fontSize:13, padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', width:90}} />
                <span style={{fontSize:12, color:'var(--text-3)', marginLeft:8}}>Durée RDV</span>
                <select value={p.duree_rdv} disabled={!p.actif}
                  onChange={e => updatePlage(p.jour, 'duree_rdv', parseInt(e.target.value))}
                  style={{fontSize:13, padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)'}}>
                  {[15,20,30,45,60,90].map(d => <option key={d} value={d}>{d} min</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
        <div style={{display:'flex', alignItems:'center', gap:12, marginTop:16}}>
          <button className="btn btn-primary" onClick={savePlages} disabled={savingPlages}>
            {savingPlages ? 'Enregistrement…' : 'Enregistrer les plages'}
          </button>
          {plagesSaved && <span style={{fontSize:13, color:'var(--green-text)'}}>✓ Enregistré</span>}
        </div>
      </div>
      <div className="card" style={{maxWidth:680, marginBottom:24}}>
        <div style={{fontSize:13, fontWeight:500, color:'var(--text-2)', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:8}}>
          Ma fiche site web
        </div>
        <p style={{fontSize:13, color:'var(--text-3)', marginTop:0, marginBottom:20, lineHeight:1.5}}>
          Ce que les visiteurs verront sur le site public. Vous seule pouvez modifier votre fiche.
        </p>

        <PhotoBlock column="photo_portrait_url" label="Photo de profil" maxDim={800} ronde={true} />
        <PhotoBlock column="photo_piece_url" label="Photo de ma pièce (optionnel)" maxDim={1200} ronde={false} />

        <div style={labelStyle}>Présentation</div>
        <div className="form-group" style={{marginBottom:14}}>
          <label>Accroche (carte d'équipe)</label>
          <textarea rows={2} maxLength={300} value={fiche.presentation_courte}
            onChange={e => setFiche({...fiche, presentation_courte: e.target.value})}
            placeholder="Une ou deux phrases qui résument votre approche." />
          <Compteur valeur={fiche.presentation_courte} max={300} />
        </div>
        <div className="form-group" style={{marginBottom:14}}>
          <label>Présentation complète (page profil)</label>
          <textarea rows={7} value={fiche.presentation_longue}
            onChange={e => setFiche({...fiche, presentation_longue: e.target.value})}
            placeholder="Votre parcours, votre approche, ce que vous proposez…" />
          <Compteur valeur={fiche.presentation_longue} max={1500} />
        </div>

        <div style={labelStyle}>Formations</div>
        <div className="form-group" style={{marginBottom:14}}>
          <label>Une formation par ligne</label>
          <textarea rows={5} value={fiche.formations}
            onChange={e => setFiche({...fiche, formations: e.target.value})}
            placeholder={"Ostéopathie pédiatrique\nOstéopathie du sport\nPrise en charge de la femme enceinte"} />
        </div>

        <div style={labelStyle}>Actualité / Congés</div>
        <div className="form-group" style={{marginBottom:10}}>
          <textarea rows={3} value={fiche.actualite}
            onChange={e => setFiche({...fiche, actualite: e.target.value})}
            placeholder="Ex : Cabinet fermé du 14 au 28 juillet. Reprise le 29 juillet." />
        </div>
        <label style={{display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:14, marginBottom:20}}>
          <input type="checkbox" checked={fiche.actualite_visible}
            onChange={e => setFiche({...fiche, actualite_visible: e.target.checked})} />
          Afficher cette actualité sur le site
        </label>

        <div style={{borderTop:'0.5px solid var(--border)', paddingTop:16, marginBottom:18}}>
          <label style={{display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:14}}>
            <input type="checkbox" checked={fiche.fiche_visible}
              onChange={e => setFiche({...fiche, fiche_visible: e.target.checked})} />
            Afficher ma fiche sur le site
          </label>
        </div>

        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <button className="btn btn-primary" onClick={saveFiche} disabled={savingFiche}>
            {savingFiche ? 'Enregistrement…' : 'Enregistrer ma fiche'}
          </button>
          {ficheSaved && <span style={{fontSize:13, color:'var(--green-text)'}}>✓ Enregistré</span>}
        </div>
      </div>
    </>
  )
}
